import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Custom auth hook for PASS Profile.
 * Returns the Supabase auth user, the users-table profile row,
 * loading state, admin flag, and a signOut helper.
 */
export function useAuth() {
  const [user, setUser] = useState(null);         // Supabase auth user
  const [profile, setProfile] = useState(null);    // users table row
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadAuth = async () => {
      try {
        // 1. Get the authenticated user from Supabase auth
        const { data: { user: authUser } } = await supabase.auth.getUser();

        if (!authUser || cancelled) {
          setLoading(false);
          return;
        }

        setUser(authUser);

        // 2. Fetch the profile row from the users table
        const { data: profileData, error: profileError } = await supabase
          .from('users')
          .select('id, email, full_name, is_admin, report_url, report_status, report_generated_at, profile_title, voice_brand_purchased, voice_brand_status, tda_purchased, share_content')
          .eq('id', authUser.id)
          .single();

        if (profileError) {
          console.error('useAuth profile fetch error:', profileError);
        }

        if (!cancelled && profileData) {
          setProfile(profileData);
          setIsAdmin(profileData.is_admin === true);
        }
      } catch (err) {
        console.error('useAuth error:', err);
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    loadAuth();

    // Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!session) {
          // User signed out
          setUser(null);
          setProfile(null);
          setIsAdmin(false);
        }
        // If user signs in, re-run the full load
        if (session?.user) {
          loadAuth();
        }
      }
    );

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, []);

  const signOut = async (redirectTo = '/') => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    window.location.href = redirectTo;
  };

  return { user, profile, loading, isAdmin, signOut };
}
